import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, LogOut, MessageSquare, Package, FileText, ExternalLink, MapPin, X, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError, API } from "@/lib/api";
import { LOGO_URL, COMPANY, currencySymbol } from "@/lib/constants";
import { StatusBadge } from "@/components/StatusBadge";
import JobFormDialog from "@/components/JobFormDialog";

const CustomerHeader = ({ user, onLogout }) => (
  <header className="bg-white border-b border-zinc-200 sticky top-0 z-20">
    <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
      <Link to="/portal" className="flex items-center gap-3">
        <img src={LOGO_URL} alt="Apex" className="h-9 w-9 object-contain" />
        <div>
          <div className="font-bold tracking-tight leading-none text-[#0056B3]">APEX</div>
          <div className="mono-label !text-[10px]">Customer portal</div>
        </div>
      </Link>
      <div className="flex items-center gap-4">
        <div className="text-right hidden sm:block">
          <div className="text-sm font-medium">{user.name}</div>
          <div className="mono-label !text-[10px]">{user.company || user.email}</div>
        </div>
        <Button variant="outline" onClick={onLogout} className="sharp border-zinc-300" data-testid="customer-logout">
          <LogOut className="h-4 w-4 mr-2" /> Sign out
        </Button>
      </div>
    </div>
  </header>
);

const JobCard = ({ job, onOpen }) => (
  <button
    type="button"
    onClick={() => onOpen(job)}
    className="text-left border border-zinc-200 bg-white p-5 hover:border-zinc-950 transition-colors w-full block"
    data-testid={`portal-job-${job.job_number}`}
  >
    <div className="flex items-start justify-between">
      <div>
        <div className="mono-label">Job #</div>
        <div className="font-mono text-xl font-medium mt-1">{job.job_number}</div>
      </div>
      <StatusBadge status={job.status} />
    </div>
    <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
      <div>
        <div className="mono-label">Pickup</div>
        <div className="mt-1">{job.pickup?.city}, {job.pickup?.state}</div>
        {job.pickup_date && <div className="text-zinc-500 text-xs mt-0.5">{job.pickup_date}</div>}
      </div>
      <div>
        <div className="mono-label">Delivery</div>
        <div className="mt-1">{job.delivery?.city}, {job.delivery?.state}</div>
        {job.appointment_date && <div className="text-zinc-500 text-xs mt-0.5">Appt: {new Date(job.appointment_date).toLocaleString()}</div>}
      </div>
    </div>
    <div className="mt-4 pt-3 border-t border-zinc-100 flex flex-wrap items-center justify-between text-xs gap-2">
      <div className="flex items-center gap-3">
        {job.po_number && <span className="mono-label">PO # <span className="font-mono normal-case tracking-normal text-zinc-950">{job.po_number}</span></span>}
        {job.tracking_url && <span className="inline-flex items-center gap-1 text-[#0056B3] font-mono text-[10px] uppercase tracking-wider"><span className="h-1.5 w-1.5 bg-[#0056B3] animate-pulse rounded-full" /> LIVE TRACKING</span>}
        {(job.pod_files || []).length > 0 && <span className="mono-label !text-emerald-800">POD · {job.pod_files.length}</span>}
      </div>
      {job.rate && <span className="font-mono">{currencySymbol(job.currency)}{job.rate.toLocaleString()} {job.currency || "USD"}</span>}
    </div>
  </button>
);

const JobsPanel = ({ jobs, onNew, loading, onOpen, blocked }) => (
  <div>
    <div className="flex items-center justify-between mb-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">My jobs</h2>
        <p className="text-sm text-zinc-600">All shipments you've booked with Apex. Click a job for full details, live tracking and POD.</p>
      </div>
      <Button
        onClick={onNew}
        disabled={blocked}
        title={blocked ? "Account limit reached — cannot create new jobs" : ""}
        className="sharp bg-[#0056B3] hover:bg-[#004494] disabled:opacity-60 disabled:cursor-not-allowed"
        data-testid="portal-new-job"
      >
        <Plus className="h-4 w-4 mr-2" /> New job request
      </Button>
    </div>
    {loading ? (
      <div className="mono-label">Loading jobs...</div>
    ) : jobs.length === 0 ? (
      <div className="border border-dashed border-zinc-300 p-16 text-center bg-white">
        <Package className="h-10 w-10 text-zinc-400 mx-auto" />
        <h3 className="mt-4 text-lg font-semibold">No jobs yet</h3>
        <p className="text-sm text-zinc-600 mt-1">Start by creating a job request with pickup and delivery details.</p>
        <Button onClick={onNew} className="mt-6 sharp bg-[#0056B3] hover:bg-[#004494]">
          <Plus className="h-4 w-4 mr-2" /> Create your first job
        </Button>
      </div>
    ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {jobs.map((j) => <JobCard key={j.id} job={j} onOpen={onOpen} />)}
      </div>
    )}
  </div>
);

const isImage = (name = "") => /\.(png|jpe?g|webp|heic|gif)$/i.test(name);

const CustomerJobDetailDialog = ({ open, onOpenChange, job }) => {
  if (!job) return null;
  const backendBase = API.replace(/\/api$/, "");
  const buildUrl = (u) => (u?.startsWith("http") ? u : `${backendBase}${u}`);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sharp max-w-3xl max-h-[90vh] overflow-y-auto border-zinc-950">
        <DialogHeader>
          <div className="flex items-start justify-between">
            <div>
              <div className="mono-label">JOB &middot; {job.status?.toUpperCase()}</div>
              <DialogTitle className="text-3xl font-bold tracking-tight font-mono mt-1">{job.job_number}</DialogTitle>
            </div>
            <StatusBadge status={job.status} />
          </div>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border border-zinc-200 p-4">
            <div className="mono-label">PICKUP</div>
            <div className="mt-2 text-sm">
              {job.pickup?.name && <div className="font-semibold">{job.pickup.name}</div>}
              <div>{job.pickup?.street}</div>
              <div>{job.pickup?.city}, {job.pickup?.state} {job.pickup?.zip}</div>
              {job.pickup_date && <div className="mono-label mt-3">DATE: <span className="font-mono normal-case tracking-normal text-zinc-950">{job.pickup_date}</span></div>}
            </div>
          </div>
          <div className="border border-zinc-200 p-4">
            <div className="mono-label">DELIVERY</div>
            <div className="mt-2 text-sm">
              {job.delivery?.name && <div className="font-semibold">{job.delivery.name}</div>}
              <div>{job.delivery?.street}</div>
              <div>{job.delivery?.city}, {job.delivery?.state} {job.delivery?.zip}</div>
              {job.appointment_date && <div className="mono-label mt-3">APPT: <span className="font-mono normal-case tracking-normal text-zinc-950">{new Date(job.appointment_date).toLocaleString()}</span></div>}
              {job.delivery_date && <div className="mono-label mt-1">DELIVERED: <span className="font-mono normal-case tracking-normal text-zinc-950">{job.delivery_date}</span></div>}
            </div>
          </div>
        </div>

        {job.tracking_url && (
          <a
            href={job.tracking_url}
            target="_blank"
            rel="noreferrer"
            data-testid="portal-tracking-link"
            className="mt-4 border-2 border-[#0056B3] bg-[#0056B3] hover:bg-[#004494] text-white p-4 flex items-center gap-3 transition-colors"
          >
            <MapPin className="h-5 w-5" />
            <div className="flex-1">
              <div className="mono-label !text-white/80">LIVE TRACKING</div>
              <div className="font-mono text-sm truncate">{job.tracking_url}</div>
            </div>
            <ExternalLink className="h-4 w-4" />
          </a>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div><span className="mono-label block">Service</span>{job.service_type || "—"}</div>
          <div><span className="mono-label block">Equipment</span>{job.equipment || "—"}</div>
          <div><span className="mono-label block">PO #</span><span className="font-mono">{job.po_number || "—"}</span></div>
          <div><span className="mono-label block">Reference</span><span className="font-mono">{job.reference || "—"}</span></div>
          <div><span className="mono-label block">Commodity</span>{job.commodity || "—"}</div>
          <div><span className="mono-label block">Pieces</span><span className="font-mono">{job.pieces ? `${job.pieces} ${job.pieces_unit || ""}` : "—"}</span></div>
          <div><span className="mono-label block">Weight</span><span className="font-mono">{job.weight ? `${job.weight} lb` : "—"}</span></div>
          <div><span className="mono-label block">Rate</span><span className="font-mono">{job.rate ? `${currencySymbol(job.currency)}${Number(job.rate).toLocaleString()} ${job.currency || "USD"}` : "—"}</span></div>
        </div>

        {(job.driver_name || job.truck_number || job.carrier_name) && (
          <div className="border border-zinc-200 p-4 bg-zinc-50">
            <div className="mono-label mb-2">CARRIER &amp; DRIVER</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              {job.carrier_name && <div><span className="mono-label block">Carrier</span>{job.carrier_name}</div>}
              <div><span className="mono-label block">Driver</span>{job.driver_name || "—"}</div>
              <div><span className="mono-label block">Truck #</span><span className="font-mono">{job.truck_number || "—"}</span></div>
              <div><span className="mono-label block">Trailer #</span><span className="font-mono">{job.trailer_number || "—"}</span></div>
            </div>
          </div>
        )}

        <div className="border border-zinc-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="mono-label">PROOF OF DELIVERY</div>
            <span className="mono-label !text-[10px]">{(job.pod_files || []).length} FILE(S)</span>
          </div>
          {(job.pod_files || []).length === 0 ? (
            <div className="text-sm text-zinc-500 italic">POD will appear here once the delivery is complete.</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {job.pod_files.map((f) => (
                <div key={f.filename} className="border border-zinc-200 bg-zinc-50" data-testid={`portal-pod-${f.filename}`}>
                  <div className="aspect-video bg-white border-b border-zinc-200 flex items-center justify-center overflow-hidden">
                    {isImage(f.original_name) ? (
                      <img src={buildUrl(f.url)} alt={f.original_name} className="w-full h-full object-cover" />
                    ) : (
                      <FileText className="h-10 w-10 text-zinc-400" />
                    )}
                  </div>
                  <div className="p-2 text-xs">
                    <div className="font-mono truncate" title={f.original_name}>{f.original_name}</div>
                    <a href={buildUrl(f.url)} target="_blank" rel="noreferrer"
                       className="mt-2 text-[#0056B3] underline inline-flex items-center">
                      <Download className="h-3 w-3 mr-1" /> Download
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {job.notes && (
          <div className="border border-zinc-200 p-4 bg-zinc-50">
            <div className="mono-label mb-1">NOTES FROM DISPATCH</div>
            <p className="text-sm whitespace-pre-wrap">{job.notes}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const InvoicesPanel = ({ invoices }) => (
  <div>
    <h2 className="text-2xl font-bold tracking-tight">Invoices</h2>
    <p className="text-sm text-zinc-600 mb-6">Every invoice generated against your jobs.</p>
    {invoices.length === 0 ? (
      <div className="border border-dashed border-zinc-300 p-16 text-center bg-white">
        <FileText className="h-10 w-10 text-zinc-400 mx-auto" />
        <p className="mt-3 text-sm text-zinc-600">No invoices yet.</p>
      </div>
    ) : (
      <div className="border border-zinc-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 mono-label border-b border-zinc-200">
            <tr>
              <th className="text-left py-3 px-4">Invoice</th>
              <th className="text-left py-3 px-4">Job</th>
              <th className="text-left py-3 px-4">Total</th>
              <th className="text-left py-3 px-4">Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv, i) => (
              <tr key={inv.id} className={i % 2 ? "bg-zinc-50" : ""} data-testid={`inv-row-${inv.invoice_number}`}>
                <td className="py-3 px-4 font-mono">{inv.invoice_number}</td>
                <td className="py-3 px-4 font-mono">{inv.job_number}</td>
                <td className="py-3 px-4 font-mono">{currencySymbol(inv.currency)}{inv.total.toLocaleString()} {inv.currency || "USD"}</td>
                <td className="py-3 px-4"><StatusBadge status={inv.status === "paid" ? "delivered" : "requested"} /></td>
                <td className="py-3 px-4 text-right">
                  <Link to={`/documents/invoice/${inv.id}`} className="text-[#0056B3] underline text-sm inline-flex items-center">
                    View <ExternalLink className="h-3 w-3 ml-1" />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const AskPanel = () => {
  const [form, setForm] = useState({ subject: "", message: "" });
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", {
        name: user.name, email: user.email, company: user.company,
        phone: user.phone, subject: form.subject, message: form.message,
      });
      toast.success("Message sent to dispatch.");
      setForm({ subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <h2 className="text-2xl font-bold tracking-tight">Ask dispatch</h2>
      <p className="text-sm text-zinc-600 mb-6">Send a question directly to the Apex operations inbox.</p>
      <form onSubmit={submit} className="space-y-4 bg-white border border-zinc-200 p-6">
        <div>
          <label className="mono-label">Subject</label>
          <Input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
            className="sharp mt-1 apex-input" data-testid="portal-msg-subject" />
        </div>
        <div>
          <label className="mono-label">Message</label>
          <Textarea required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
            className="sharp mt-1 apex-input" data-testid="portal-msg-body" />
        </div>
        <Button disabled={busy} className="sharp bg-[#0056B3] hover:bg-[#004494]" data-testid="portal-msg-submit">
          <MessageSquare className="h-4 w-4 mr-2" /> {busy ? "Sending..." : "Send message"}
        </Button>
      </form>
    </div>
  );
};

export default function CustomerPortal() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailJob, setDetailJob] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [credit, setCredit] = useState(null);

  const openDetail = async (j) => {
    // Fetch fresh single-job (has pod_files) — the list already includes them but re-fetch for accuracy
    try {
      const { data } = await api.get(`/jobs/${j.id}`);
      setDetailJob(data);
    } catch {
      setDetailJob(j);
    }
    setDetailOpen(true);
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [j, i, cr] = await Promise.all([
        api.get("/jobs"),
        api.get("/invoices"),
        api.get("/me/credit"),
      ]);
      setJobs(j.data);
      setInvoices(i.data);
      setCredit(cr.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const doLogout = async () => { await logout(); nav("/"); };

  return (
    <div className="min-h-screen bg-zinc-50">
      <CustomerHeader user={user} onLogout={doLogout} />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="mb-6">
          <div className="mono-label">CUSTOMER PORTAL &middot; {COMPANY.name}</div>
          <h1 className="text-4xl font-bold tracking-tight mt-2">Welcome back, {user.name.split(" ")[0]}.</h1>
        </div>

        {credit && credit.credit_limit > 0 && (
          <div
            data-testid="portal-credit-banner"
            className={`mb-6 border p-4 ${
              credit.credit_over_limit
                ? "border-rose-400 bg-rose-50"
                : credit.credit_utilization_pct > 80
                  ? "border-amber-400 bg-amber-50"
                  : "border-zinc-200 bg-white"
            }`}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className={`mono-label ${credit.credit_over_limit ? "!text-rose-800" : credit.credit_utilization_pct > 80 ? "!text-amber-800" : ""}`}>
                  {credit.credit_over_limit ? "ACCOUNT LIMIT REACHED" : "ACCOUNT CREDIT"}
                </div>
                <div className="mt-1 font-mono text-sm">
                  {currencySymbol(credit.credit_currency)}{credit.outstanding_total.toLocaleString()} outstanding of {currencySymbol(credit.credit_currency)}{credit.credit_limit.toLocaleString()} {credit.credit_currency} limit
                  {credit.outstanding_count > 0 && <> · {credit.outstanding_count} unpaid invoice{credit.outstanding_count > 1 ? "s" : ""}</>}
                </div>
                {credit.credit_over_limit && (
                  <p className="text-sm text-rose-800 mt-2">
                    New job requests are paused. Please clear outstanding invoices or contact Apex dispatch to raise your limit.
                  </p>
                )}
              </div>
              <div className="text-right shrink-0 w-40">
                <div className="mono-label !text-[10px]">{credit.credit_utilization_pct}%</div>
                <div className="mt-1 h-2 bg-zinc-200 relative">
                  <div
                    className={`absolute inset-y-0 left-0 ${credit.credit_over_limit ? "bg-rose-600" : credit.credit_utilization_pct > 80 ? "bg-amber-500" : "bg-emerald-600"}`}
                    style={{ width: `${Math.min(100, credit.credit_utilization_pct)}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        <Tabs defaultValue="jobs" className="w-full">
          <TabsList className="sharp bg-white border border-zinc-200 h-11 p-0">
            <TabsTrigger value="jobs" className="sharp px-5 data-[state=active]:bg-zinc-950 data-[state=active]:text-white" data-testid="tab-jobs">Jobs</TabsTrigger>
            <TabsTrigger value="invoices" className="sharp px-5 data-[state=active]:bg-zinc-950 data-[state=active]:text-white" data-testid="tab-invoices">Invoices</TabsTrigger>
            <TabsTrigger value="ask" className="sharp px-5 data-[state=active]:bg-zinc-950 data-[state=active]:text-white" data-testid="tab-ask">Ask dispatch</TabsTrigger>
          </TabsList>
          <div className="mt-6">
            <TabsContent value="jobs"><JobsPanel jobs={jobs} loading={loading} onNew={() => setDialogOpen(true)} onOpen={openDetail} blocked={credit?.credit_over_limit} /></TabsContent>
            <TabsContent value="invoices"><InvoicesPanel invoices={invoices} /></TabsContent>
            <TabsContent value="ask"><AskPanel /></TabsContent>
          </div>
        </Tabs>
      </div>

      <JobFormDialog open={dialogOpen} onOpenChange={setDialogOpen} onCreated={loadAll} />
      <CustomerJobDetailDialog open={detailOpen} onOpenChange={setDetailOpen} job={detailJob} />
    </div>
  );
}
